import Role from "../../entities/role";

/**
 * Role actions
 *
 * Call order:
 *
 * ROLE_SAVE_QUEUE:	adds the item to the save queue
 * --> ROLE_SAVE:	modifies the state
 * --> ROLE_SAVE_SUCCESS: if all went well
 * --> ROLE_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail ROLE_SAVE_QUEUE is during the call to ROLE_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at ROLE_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: ROLE_SAVE_SUCCESS, ROLE_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or ROLE_PENDING_CHECK (it left the queue).
 */

export const ROLE_FETCH_ONE_QUEUE: any = 'ROLE_FETCH_ONE_QUEUE';
export const ROLE_FETCH_SUCCESS: any = 'ROLE_FETCH_SUCCESS';
export const ROLE_FETCH_FAILURE: any = 'ROLE_FETCH_FAILURE';
export const ROLE_FETCH_ONE_SUCCESS: any = 'ROLE_FETCH_ONE_SUCCESS';
export const ROLE_FETCH_ONE_FAILURE: any = 'ROLE_FETCH_ONE_FAILURE';

export const ROLE_SAVE: any = 'ROLE_SAVE';
export const ROLE_SAVE_QUEUE: any = 'ROLE_SAVE_QUEUE';
export const ROLE_SAVE_SUCCESS: any = 'ROLE_SAVE_SUCCESS';
export const ROLE_SAVE_FAILURE: any = 'ROLE_SAVE_FAILURE';

export const ROLE_SAVE_MANY: any = 'ROLE_SAVE_MANY';
export const ROLE_SAVE_MANY_QUEUE: any = 'ROLE_SAVE_MANY_QUEUE';
export const ROLE_SAVE_MANY_SUCCESS: any = 'ROLE_SAVE_MANY_SUCCESS';
export const ROLE_SAVE_MANY_FAILURE: any = 'ROLE_SAVE_MANY_FAILURE';

export const ROLE_REMOVE: any = 'ROLE_REMOVE';
export const ROLE_REMOVE_QUEUE: any = 'ROLE_REMOVE_QUEUE';
export const ROLE_REMOVE_SUCCESS: any = 'ROLE_REMOVE_SUCCESS';
export const ROLE_REMOVE_FAILURE: any = 'ROLE_REMOVE_FAILURE';

export const ROLE_RESTORE: any = 'ROLE_RESTORE';
export const ROLE_RESTORE_QUEUE: any = 'ROLE_RESTORE_QUEUE';
export const ROLE_RESTORE_SUCCESS: any = 'ROLE_RESTORE_SUCCESS';
export const ROLE_RESTORE_FAILURE: any = 'ROLE_RESTORE_FAILURE';

export const ROLE_RESTORE_MANY: any = 'ROLE_RESTORE_MANY';
export const ROLE_REMOVE_MANY: any = 'ROLE_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const ROLE_SAVE_REVERT: any = 'ROLE_SAVE_REVERT';
export const ROLE_PENDING_CHECK: any = 'ROLE_PENDING_CHECK';

/* Fetch actions */
export const fetchRoleSuccess = (roles: Role[], partial: boolean, lastFetch: number) => ({type: ROLE_FETCH_SUCCESS, roles, partial, lastFetch});
export const fetchRoleFailure = (err: any) => {
		console.error('RoleAction: fetchRoleFailure', err);
		return {type: ROLE_FETCH_FAILURE, err};
};
export const fetchOneRoleQueue = (roleId: string, sentAddedOn?: number) => ({type: ROLE_FETCH_ONE_QUEUE, roleId, sentAddedOn});
export const fetchOneRoleSuccess = (role: Role | null, sentAddedOn?: number, roleId: string = role?.id) => ({type: ROLE_FETCH_ONE_SUCCESS, roleId, role, sentAddedOn});
export const fetchOneRoleFailure = (id: string, err: any) => {
		console.error('RoleAction: fetchOneRoleFailure', err);
		return {type: ROLE_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveRole = (role: Role) => {
    // console.debug('⮂ ROLE_SAVE action', role?.id);
    return {type: ROLE_SAVE, role};
}
export const saveRoleQueue = (role: Role, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ROLE_SAVE_QUEUE action', role?.id);
    return {type: ROLE_SAVE_QUEUE, role, onQueued};
}
export const saveRoleSuccess = (role: Role, sentAddedOn?: number) => {
    // console.debug('⮂ ROLE_SAVE_SUCCESS action', role?.id);
    return {type: ROLE_SAVE_SUCCESS, role, sentAddedOn};
}
export const saveRoleFailure = (role: Role, status: number, message: string) => {
    console.warn(`⮂ ROLE_SAVE_FAILURE error saving role: HTTP ${status}: ${message}`, role?.id);
    return {type: ROLE_SAVE_FAILURE, role, status, message};
};

/* SaveMany actions */
export const saveManyRoles = (roles: Role[]) => {
    // console.debug('⮂ ROLE_SAVE_MANY action', roles?.map(role => role?.id).join(','));
    return {type: ROLE_SAVE_MANY, roles};
}
export const saveManyRolesQueue = (roles: Role[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ROLE_SAVE_MANY_QUEUE action', roles?.map(role => role.id).join(','));
    return {type: ROLE_SAVE_MANY_QUEUE, roles, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `roles` (OWA-1018) */
export const saveManyRolesSuccess = (roles: Role[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ ROLE_SAVE_MANY_SUCCESS action', roles?.map(role => role.id).join(','));
    return {type: ROLE_SAVE_MANY_SUCCESS, roles, sentAddedOns};
}
export const saveManyRolesFailure = (roles: Role[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_ROLES_FAILURE Error saving roles: HTTP ${status}: ${message}`, roles?.map(role => role.id).join(','));
		return {type: ROLE_SAVE_MANY_FAILURE, roles, status, message};
};

/* Remove actions */
export const removeRole = (role: Role) => ({ type: ROLE_REMOVE, role });
export const removeRoleQueue = (role: Role) => ({ type: ROLE_REMOVE_QUEUE, role });
export const removeRoleSuccess = (role: Role) => ({ type: ROLE_REMOVE_SUCCESS, role });
export const removeRoleFailure = (role: Role, status: number, message: string) => {
	console.error(`Redux: removeRoleFailure Error removing role: HTTP ${status}: ${message}`);
	return ({ type: ROLE_REMOVE_FAILURE, role, status, message });
};

/* Restore actions */
export const restoreRole = (role: Role) => ({ type: ROLE_RESTORE, role });
export const restoreRoleQueue = (role: Role) => ({ type: ROLE_RESTORE_QUEUE, role });
export const restoreRoleSuccess = (role: Role, sentAddedOn?: number) => ({ type: ROLE_RESTORE_SUCCESS, role, sentAddedOn });
export const restoreRoleFailure = (role: Role, status: number, message: string) => {
	console.error(`Redux: restoreRoleFailure Error restoring role: HTTP ${status}: ${message}`);
	return ({ type: ROLE_RESTORE_FAILURE, role, status, message });
};

/* RestoreMany actions */
export const restoreManyRoles = (roles: Role[]) => ({ type: ROLE_RESTORE_MANY, roles });

/* RemoveMany actions */
export const removeManyRoles = (roles: Role[]) => ({ type: ROLE_REMOVE_MANY, roles });

/* Pending save actions (OWA-1018) */
/**
 * The save `role` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertRoleSave = (role: Role, options: {sentAddedOn?: number, current?: Role | null, keep?: boolean} = {}) =>
    ({type: ROLE_SAVE_REVERT, role, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkRolePendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: ROLE_PENDING_CHECK, live, refused, readAt});